import React, { useRef, useState, useEffect } from 'react';
import ReactPannellum, { getViewer } from 'react-pannellum';
import { FaChevronLeft, FaChevronRight, FaArrowRight } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import LoadingLogo from '../components/LoadingLogo';
import FunFact from '../components/FunFact';
import './Panorama.css';

const Panorama = () => {
  const imageUrl = '/images/panorama.jpg';
  // eslint-disable-next-line no-unused-vars
  const pannellumRef = useRef(null);
  const panoramaContainerRef = useRef(null);
  const isInitializedRef = useRef(false); // Prevent double initialization in StrictMode
  const [isLoaded, setIsLoaded] = useState(false);
  const [isViewSwitched, setIsViewSwitched] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [showLoading, setShowLoading] = useState(true);
  const [currentViewIndex, setCurrentViewIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isNavigatingAway, setIsNavigatingAway] = useState(false);
  const [panoramaKey, setPanoramaKey] = useState(0);
  const hasLoadedOnceRef = useRef(false);
  const viewSwitchTimeoutRef = useRef(null);
  const loadingTimeoutRef = useRef(null);
  const resizeFrameRef = useRef(null);
  
  // Minimum swipe distance in pixels
  const minSwipeDistance = 50;
  
  // Predefined view positions with zoom (hfov) parameter and descriptions (with Chinese translations)
  const viewPositions = [
    { 
      name: '游戏流', // Game Feed
      englishName: 'Game Feed',
      pitch: 0, 
      yaw: 0, 
      hfov: 80,
      pcHfov: 80,
      mobileHfov: 60,
      description: '发现下一款好玩的游戏',
      englishDescription: 'Discover the latest gaming news and updates from the community',
      link: '/feed'
    },
    { 
      name: '档案室', // Archives
      englishName: 'ReconDB',
      pitch: -5, 
      yaw: 42, 
      hfov: 90,
      pcHfov: 90,
      mobileHfov: 50,
      description: '探索我们的档案资源',
      englishDescription: 'Explore our extensive database of retro games and information',
      link: '/archives'
    },
    { 
      name: '游戏库', // Games
      englishName: 'Games',
      pitch: -25, 
      yaw: 110, 
      hfov: 95,
      pcHfov: 95,
      mobileHfov: 60,
      description: '浏览我们收藏的全部经典游戏',
      englishDescription: 'Browse through our collection of classic and modern games',
      link: '/games'
    },
    { 
      name: '联机大厅', // Multiplayer
      englishName: 'Multiplayer',
      pitch: 0, 
      yaw: 195, 
      hfov: 95,
      pcHfov: 95,
      mobileHfov: 60,
      description: '与其他玩家连接并加入多人游戏会话',
      englishDescription: 'Connect with other gamers and join multiplayer sessions',
      link: 'https://netplay.rgm.games'
    },
    { 
      name: '关于我们', // Join Us
      englishName: 'Join Us',
      pitch: 0, 
      yaw: 270, 
      hfov: 85,
      pcHfov: 85,
      mobileHfov: 60,
      description: '了解我们的志业，支持或加入我们！',
      englishDescription: 'Become part of our growing retro gaming community',
      link: '/about'
    }
  ];


  
  // Define the config object with all our settings
  // Use the first viewPosition's values for initial pitch, yaw, and hfov
  const config = {
    autoLoad: true,
    showZoomCtrl: false,
    showFullscreenCtrl: false,
    pitch: viewPositions[0].pitch,
    yaw: viewPositions[0].yaw,
    hfov: viewPositions[0].hfov,
    draggable: false,  // Disable user drag functionality
    mouseZoom: false,  // Disable mouse zoom
    keyboardZoom: false, // Disable keyboard zoom
    disableKeyboardCtrl: true // Disable keyboard controls
  };

  // Function to detect if device is mobile
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
  };

  // Function to reset component state for fresh initialization
  const resetComponentState = () => {
    console.log('Resetting panorama component state...');
    
    // Clear any pending timeouts
    if (viewSwitchTimeoutRef.current) {
      clearTimeout(viewSwitchTimeoutRef.current);
      viewSwitchTimeoutRef.current = null;
    }
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    
    setIsLoaded(false);
    setIsViewSwitched(false);
    setIsFadingOut(false);
    setShowLoading(true);
    setCurrentViewIndex(0);
    setIsNavigatingAway(false);
    isInitializedRef.current = false;
    // Don't reset hasLoadedOnceRef - we want to remember we've loaded before
    
    // Force ReactPannellum to remount by changing its key
    setPanoramaKey(prev => prev + 1);
    
    // Remove any transition classes that might be stuck
    const panoramaContainer = document.querySelector('.panorama-container');
    if (panoramaContainer) {
      panoramaContainer.classList.remove('zooming-out', 'fade-to-black');
    }
  };

  // Function to switch to a predefined view
  const switchView = (position, index) => {
    if (isLoaded) {
      try {
        const viewer = getViewer('1');
        if (viewer) {
          // Determine which HFOV to use based on device type
          const hfovToUse = isMobile() ? position.mobileHfov : position.pcHfov;
          
          // Use lookAt method to change view with animation
          viewer.lookAt(
            position.pitch,  // pitch
            position.yaw,    // yaw
            hfovToUse || position.hfov,   // use device-specific hfov or fall back to default
            1000,            // animation duration in ms
            () => {
              console.log(`Switched to ${position.name}`);
              // Ensure view switched state is set after successful view change
              if (!isViewSwitched) {
                setIsViewSwitched(true);
              }
            }
          );
          setCurrentViewIndex(index);
        } else {
          console.error('Viewer not available for view switch');
          // Fallback: mark as switched anyway to prevent stuck loading
          if (!isViewSwitched) {
            setIsViewSwitched(true);
          }
        }
      } catch (error) {
        console.error('Error switching view:', error);
        // Fallback: mark as switched anyway to prevent stuck loading
        if (!isViewSwitched) {
          setIsViewSwitched(true);
        }
      }
    }
  };

  // Function to zoom forward and then navigate to a section
  const zoomAndNavigate = (position, index) => {
    if (isLoaded) {
      try {
        const viewer = getViewer('1');
        if (viewer) {
          // First, switch to the view if not already there
          if (currentViewIndex !== index) {
            switchView(position, index);
            // Wait for the view switch to complete before zooming
            setTimeout(() => {
              performZoomAndNavigate(position);
            }, 1100); // Wait for switch animation to complete
          } else {
            // Already at the correct view, just zoom and navigate
            performZoomAndNavigate(position);
          }
        }
      } catch (error) {
        console.error('Error in zoom and navigate:', error);
        // Fallback to direct navigation if zoom fails
        if (position.link.startsWith('http')) {
          window.location.replace(position.link);
        } else {
          window.location.href = position.link;
        }
      }
    }
  };

  // Helper function to perform the zoom animation and navigation
  const performZoomAndNavigate = (position) => {
    try {
      const viewer = getViewer('1');
      if (viewer) {
        // Calculate zoom-in hfov (smaller value = more zoomed in)
        const currentHfov = isMobile() ? position.mobileHfov : position.pcHfov;
        const zoomHfov = Math.max(currentHfov * 0.4, 15); // More aggressive zoom - 40% of current view, minimum 15 degrees
        
        // Add fade-out class to UI elements
        const panoramaContainer = document.querySelector('.panorama-container');
        if (panoramaContainer) {
          panoramaContainer.classList.add('zooming-out');
        }
        
        // Start zoom animation (slower)
        viewer.lookAt(
          position.pitch,
          position.yaw,
          zoomHfov,
          1200, // Longer zoom animation duration
          () => {
            // This callback is for when zoom completes
          }
        );
        
        // Start fade to black after UI elements fade out
        setTimeout(() => {
          if (panoramaContainer) {
            panoramaContainer.classList.add('fade-to-black');
          }
        }, 400); // Start fade to black after UI fade
        
        // Navigate after fade to black completes
        setTimeout(() => {
          setIsNavigatingAway(true);
          if (position.link.startsWith('http')) {
            window.location.replace(position.link);
          } else {
            window.location.href = position.link;
          }
        }, 1000); // Navigate after fade to black
      }
    } catch (error) {
      console.error('Error in zoom animation:', error);
      // Fallback to direct navigation
      if (position.link.startsWith('http')) {
        window.location.replace(position.link);
      } else {
        window.location.href = position.link;
      }
    }
  };
  
  // Navigate to previous view
  const prevView = () => {
    const newIndex = (currentViewIndex - 1 + viewPositions.length) % viewPositions.length;
    switchView(viewPositions[newIndex], newIndex);
  };
  
  // Navigate to next view
  const nextView = () => {
    const newIndex = (currentViewIndex + 1) % viewPositions.length;
    switchView(viewPositions[newIndex], newIndex);
  };
  
  // Handle touch events for swipe gestures
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      nextView();
    } else if (isRightSwipe) {
      prevView();
    }
  };

  // Handle loading state transitions
  useEffect(() => {
    if (isLoaded && isViewSwitched) {
      // Start fade out animation
      setIsFadingOut(true);
      
      // Hide loading screen after fade animation completes
      const timer = setTimeout(() => {
        setShowLoading(false);
      }, 500); // Match this with CSS transition duration
      
      return () => clearTimeout(timer);
    }
  }, [isLoaded, isViewSwitched]);

  // Failsafe: Force show panorama if loading takes too long
  useEffect(() => {
    loadingTimeoutRef.current = setTimeout(() => {
      if (!isLoaded || !isViewSwitched) {
        console.warn('Loading timeout - forcing panorama to show');
        if (!isLoaded) setIsLoaded(true);
        if (!isViewSwitched) setIsViewSwitched(true);
      }
    }, 8000); // 8 second timeout - give more time for panorama to fully load
    
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'ArrowLeft') {
        prevView();
      } else if (event.key === 'ArrowRight') {
        nextView();
      }
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyPress);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewIndex]); // nextView and prevView are intentionally excluded

  // Handle viewport height for mobile browsers
  useEffect(() => {
    const resizeViewer = () => {
      if (resizeFrameRef.current) {
        cancelAnimationFrame(resizeFrameRef.current);
      }

      // Wait until the viewport CSS and flex layout have both been applied.
      resizeFrameRef.current = requestAnimationFrame(() => {
        resizeFrameRef.current = requestAnimationFrame(() => {
          const viewer = getViewer('1');
          if (viewer && typeof viewer.resize === 'function') {
            viewer.resize();
          }
          resizeFrameRef.current = null;
        });
      });
    };

    const setViewportHeight = () => {
      // Calculate the actual viewport height and set CSS custom property
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      resizeViewer();
    };

    // Set initial viewport height
    setViewportHeight();

    // Pannellum only listens for window resizes. On mobile, the flex container can
    // change size independently while browser chrome and the navbar settle.
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(resizeViewer)
      : null;
    if (resizeObserver && panoramaContainerRef.current) {
      resizeObserver.observe(panoramaContainerRef.current);
    }

    // Update on all mobile viewport changes, including Safari's visual viewport.
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', setViewportHeight);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', setViewportHeight);
    }

    // Cleanup event listeners on component unmount
    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('orientationchange', setViewportHeight);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', setViewportHeight);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (resizeFrameRef.current) {
        cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }
    };
  }, []);

  // Prevent Safari mobile pull-to-refresh and navbar hiding
  useEffect(() => {
    let lastTouchY = 0;
    
    const preventPullToRefresh = (e) => {
      const touch = e.touches[0];
      const currentY = touch.clientY;
      
      // Get the scrollable element
      const target = e.target;
      const scrollableParent = target.closest('.panorama-container, .panorama-page-wrapper');
      
      // If we're at the top of the page and trying to scroll down, prevent it
      if (scrollableParent) {
        const scrollTop = scrollableParent.scrollTop || window.pageYOffset || document.documentElement.scrollTop;
        
        // Prevent pull-to-refresh when at the top and pulling down
        if (scrollTop === 0 && currentY > lastTouchY) {
          e.preventDefault();
        }
      }
      
      lastTouchY = currentY;
    };
    
    const resetTouch = () => {
      lastTouchY = 0;
    };
    
    // Add passive: false to allow preventDefault
    document.addEventListener('touchstart', resetTouch, { passive: true });
    document.addEventListener('touchmove', preventPullToRefresh, { passive: false });
    document.addEventListener('touchend', resetTouch, { passive: true });
    
    return () => {
      document.removeEventListener('touchstart', resetTouch);
      document.removeEventListener('touchmove', preventPullToRefresh);
      document.removeEventListener('touchend', resetTouch);
    };
  }, []);

  // Handle page visibility and browser navigation
  useEffect(() => {
    let hasBeenHidden = false;
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is being hidden, mark it
        hasBeenHidden = true;
      } else if (hasBeenHidden && !isNavigatingAway && hasLoadedOnceRef.current) {
        // Page is becoming visible after being hidden (potential back navigation)
        // Only reset if panorama was working before but isn't loaded now
        if (!isLoaded || !isViewSwitched) {
          console.log('Page restored from hidden state, panorama needs reset...');
          setTimeout(() => {
            resetComponentState();
          }, 100);
        }
        hasBeenHidden = false;
      }
    };

    const handlePageShow = (event) => {
      // Only handle if page was restored from cache (back/forward navigation)
      if (event.persisted) {
        console.log('Page restored from cache, reinitializing panorama...');
        setTimeout(() => {
          resetComponentState();
        }, 100);
      }
    };

    const handleBeforeUnload = () => {
      setIsNavigatingAway(true);
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup event listeners on component unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isLoaded, isViewSwitched, isNavigatingAway]);

  // Handle initial component mount
  useEffect(() => {
    // Reset navigation state on mount
    setIsNavigatingAway(false);
    
    // Cleanup timeouts on unmount
    return () => {
      if (viewSwitchTimeoutRef.current) {
        clearTimeout(viewSwitchTimeoutRef.current);
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [panoramaKey]); // Re-run when panorama key changes

  return (
    <div className="panorama-page-wrapper">
      <h1 className="seo-visually-hidden">怀旧游戏博物馆：经典游戏在线玩与电子游戏历史档案</h1>
      <p className="seo-visually-hidden">浏览经典电子游戏、游戏机历史、游戏手册、报纸报道、逆向工程与中国玩家文化档案。</p>
      <div className="panorama-page">
        <Navbar />
        {showLoading && (
          <div className={`loading-container ${isFadingOut ? 'fade-out' : ''}`}>
            <LoadingLogo text="载入中..." />
            <FunFact />
          </div>
        )}
        <div 
          ref={panoramaContainerRef}
          className={`panorama-container ${(!isLoaded || !isViewSwitched) ? 'hidden' : ''}`}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
      <div className="navigation-controls">
        <button 
          className="nav-button nav-button-left"
          onClick={prevView}
          aria-label="Previous view"
        >
          <FaChevronLeft />
        </button>
        
        <button 
          className="nav-button nav-button-right"
          onClick={nextView}
          aria-label="Next view"
        >
          <FaChevronRight />
        </button>
      </div>
      
      <div className="panorama-swipe-indicator">
        <span className="chinese">滑动</span>
        <span className="swipe-icon">⟷</span>
        <span className="chinese">导航</span>
      </div>
      
      <div className="view-info-panel">
        <h2 className="view-title chinese">{viewPositions[currentViewIndex].name}</h2>
        <p className="view-description chinese">{viewPositions[currentViewIndex].description}</p>
        <button 
          onClick={() => zoomAndNavigate(viewPositions[currentViewIndex], currentViewIndex)}
          className="enter-button chinese"
        >
          进入 <FaArrowRight className="enter-icon" />
        </button>
      </div>
      
      <div className="view-indicators">
        {viewPositions.map((position, index) => (
          <button 
            key={index} 
            onClick={() => {
              if (index === currentViewIndex) {
                // If clicking on current view, zoom and navigate
                zoomAndNavigate(position, index);
              } else {
                // If clicking on different view, just switch to it
                switchView(position, index);
              }
            }}
            className={`indicator-dot ${index === currentViewIndex ? 'active' : ''}`}
            aria-label={`View ${position.name}`}
          />
        ))}
      </div>
      
      <ReactPannellum
        key={panoramaKey}
        id="1"
        sceneId="firstScene"
        style={{width: "100%", height: "100%"}}
        imageSource={imageUrl}
        config={config}
        onPanoramaLoaded={() => {
          // Prevent double initialization in React.StrictMode
          if (isInitializedRef.current) {
            return;
          }
          isInitializedRef.current = true;
          
          console.log('panorama loaded - starting initialization');
          hasLoadedOnceRef.current = true; // Mark that we've successfully loaded
          
          // Ensure container is visible and clean
          const panoramaContainer = document.querySelector('.panorama-container');
          if (panoramaContainer) {
            panoramaContainer.classList.remove('zooming-out', 'fade-to-black');
          }
          
          // Initialize with first view with retry logic
          const initializeView = (retryCount = 0) => {
            try {
              const viewer = getViewer('1');
              if (viewer) {
                const position = viewPositions[0];
                const hfovToUse = isMobile() ? position.mobileHfov : position.pcHfov;

                // The mobile viewport / flex height may have changed while the
                // image loaded. Sync the WebGL buffer and projection before the
                // first frame is revealed.
                viewer.resize();
                
                viewer.lookAt(
                  position.pitch,
                  position.yaw,
                  hfovToUse || position.hfov,
                  100, // Short animation ensures callback fires reliably
                  () => {
                    console.log('Initial view set successfully - panorama ready');
                    // Only now mark as loaded and view switched - panorama is fully ready
                    setIsLoaded(true);
                    setIsViewSwitched(true);
                    setCurrentViewIndex(0);
                    
                    // Clear any existing loading timeout
                    if (loadingTimeoutRef.current) {
                      clearTimeout(loadingTimeoutRef.current);
                      loadingTimeoutRef.current = null;
                    }
                  }
                );
                
                // Fallback: Set states after a reasonable time if callback doesn't fire
                if (viewSwitchTimeoutRef.current) {
                  clearTimeout(viewSwitchTimeoutRef.current);
                }
                viewSwitchTimeoutRef.current = setTimeout(() => {
                  if (!isLoaded || !isViewSwitched) {
                    console.log('View initialization timeout - forcing ready state');
                    setIsLoaded(true);
                    setIsViewSwitched(true);
                    
                    // Clear loading timeout
                    if (loadingTimeoutRef.current) {
                      clearTimeout(loadingTimeoutRef.current);
                      loadingTimeoutRef.current = null;
                    }
                  }
                }, 1500);
              } else if (retryCount < 5) {
                // Retry if viewer not ready yet - increased to 5 retries
                console.log(`Viewer not ready, retry ${retryCount + 1}/5`);
                setTimeout(() => initializeView(retryCount + 1), 300);
              } else {
                // Give up and show panorama anyway
                console.error('Failed to initialize view after retries');
                setIsLoaded(true);
                setIsViewSwitched(true);
                
                // Clear loading timeout
                if (loadingTimeoutRef.current) {
                  clearTimeout(loadingTimeoutRef.current);
                  loadingTimeoutRef.current = null;
                }
              }
            } catch (error) {
              console.error('Error initializing view:', error);
              if (retryCount < 5) {
                setTimeout(() => initializeView(retryCount + 1), 300);
              } else {
                // Show panorama anyway to prevent black screen
                setIsLoaded(true);
                setIsViewSwitched(true);
                
                // Clear loading timeout
                if (loadingTimeoutRef.current) {
                  clearTimeout(loadingTimeoutRef.current);
                  loadingTimeoutRef.current = null;
                }
              }
            }
          };
          
          // Start initialization after small delay to ensure viewer is ready
          setTimeout(() => initializeView(), 200);
        }}
      />

      
      </div>
    </div>
    </div>
  );
};

export default Panorama;
